import React, {useCallback, useEffect, useMemo, useState} from "react";
import {LoginPage} from "./components/LoginPage.jsx";
import {LoadingState} from "./components/LoadingState.jsx";
import {PortfolioDetail} from "./components/PortfolioDetail.jsx";
import {PortfoliosPage} from "./components/PortfoliosPage.jsx";
import {StrategiesPage} from "./components/StrategiesPage.jsx";
import {StrategyDetail} from "./components/StrategyDetail.jsx";

function routeFromLocation() {
    const hash = window.location.hash.replace(/^#/, "");
    const hashParts = hash.split("/").filter(Boolean);
    if (hashParts[0] === "strategy" && hashParts[1]) return {view: "strategies", strategy: decodeURIComponent(hashParts.slice(1).join("/"))};
    if (hashParts[0] === "portfolio" && hashParts[1]) return {view: "portfolios", portfolio: decodeURIComponent(hashParts.slice(1).join("/"))};
    if (hashParts[0] === "strategies") return {view: "strategies"};
    if (hashParts[0] === "portfolios") return {view: "portfolios"};
    const pathParts = window.location.pathname.split("/").filter(Boolean);
    if (pathParts[0] === "strategies" && pathParts[1]) return {view: "strategies", strategy: decodeURIComponent(pathParts.slice(1).join("/"))};
    if (pathParts[0] === "portfolios" && pathParts[1]) return {view: "portfolios", portfolio: decodeURIComponent(pathParts.slice(1).join("/"))};
    return {view: "portfolios"};
}

function viewHash(view) {
    return `#${view}`;
}

export function App() {
    const [session, setSession] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);
    const [authError, setAuthError] = useState(null);
    const [route, setRoute] = useState(routeFromLocation);
    const [analysisWindow] = useState("ytd");
    const [search, setSearch] = useState("");
    const [refreshKey, setRefreshKey] = useState(0);
    const [refreshed, setRefreshed] = useState("Loading registry snapshot…");

    const loadSession = useCallback(async () => {
        setAuthLoading(true);
        setAuthError(null);
        try {
            const response = await fetch("/api/auth/me", {credentials: "include", cache: "no-store"});
            const body = await response.json().catch(() => ({}));
            if (response.status === 401) {
                setSession(null);
                return;
            }
            if (!response.ok) {
                throw new Error(body.error || "Unable to verify your session.");
            }
            setSession(body);
        } catch (error) {
            setSession(null);
            setAuthError(error?.message || "Unable to verify your session.");
        } finally {
            setAuthLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadSession();
    }, [loadSession]);
    useEffect(() => {
        if (!session) return undefined;
        const onHashChange = () => setRoute(routeFromLocation());
        window.addEventListener("hashchange", onHashChange);
        const timer = window.setInterval(() => setRefreshKey((key) => key + 1), 15000);
        return () => {
            window.removeEventListener("hashchange", onHashChange);
            window.clearInterval(timer);
        };
    }, [session]);
    const onRefreshed = useCallback((message) => setRefreshed(message), []);
    const signOut = async () => {
        await fetch("/api/auth/logout", {method: "POST", credentials: "include"});
        setSession(null);
    };
    const goToView = (view) => {
        window.location.hash = viewHash(view);
        setRoute({view});
    };
    const content = useMemo(() => {
        if (route.strategy) return (
            <StrategyDetail
                strategyRef={route.strategy} analysisWindow={analysisWindow} refreshKey={refreshKey}
                onBack={() => goToView("strategies")} onRefreshed={onRefreshed}
            />
        );
        if (route.portfolio) return (
            <PortfolioDetail
                id={route.portfolio} analysisWindow={analysisWindow} refreshKey={refreshKey}
                onBack={() => goToView("portfolios")} onRefreshed={onRefreshed}
            />
        );
        if (route.view === "strategies") return (
            <StrategiesPage
                search={search} analysisWindow={analysisWindow} refreshKey={refreshKey}
                onRefreshed={onRefreshed}
            />
        );
        return <PortfoliosPage analysisWindow={analysisWindow} refreshKey={refreshKey} onRefreshed={onRefreshed}/>;
    }, [analysisWindow, onRefreshed, refreshKey, route, search]);
    if (authLoading) {
        return <main className="auth-shell"><LoadingState message="Checking your Wisp Capital session…"/></main>;
    }
    if (authError) {
        return (
            <main className="auth-shell">
                <section className="auth-card auth-status">
                    <div className="eyebrow">Wisp Capital</div>
                    <h1>Strategy Registry</h1>
                    <p className="auth-error" role="alert">{authError}</p>
                    <button className="button auth-submit" onClick={() => void loadSession()} type="button">Try again</button>
                </section>
            </main>
        );
    }
    if (!session) return <LoginPage onAuthenticated={loadSession}/>;

    return (
        <main className="shell">
            <header className="registry-header">
                <div>
                    <div className="eyebrow">Wisp Capital</div>
                    <h1>Strategy Registry</h1>
                </div>
                <div className="header-actions">
                    <div className="subtle refreshed" aria-live="polite">{refreshed}</div>
                    <button className="button secondary sign-out" onClick={() => void signOut()} type="button">Sign out</button>
                </div>
            </header>
            <div className="toolbar">
                <nav className="tabs" role="tablist" aria-label="Registry views">
                    <button
                        className={`tab ${route.view === "portfolios" ? "active" : ""}`} role="tab" aria-selected={route.view === "portfolios"}
                        onClick={() => goToView("portfolios")}
                    >
                        Portfolios
                    </button>
                    <button
                        className={`tab ${route.view === "strategies" ? "active" : ""}`} role="tab" aria-selected={route.view === "strategies"}
                        onClick={() => goToView("strategies")}
                    >
                        Strategies
                    </button>
                </nav>
                <input
                    className="search" value={search} onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search strategies, owners, descriptions" aria-label="Search strategies, owners, descriptions"
                />
                <div className="ranges" aria-label="Analysis window">
                    <span className="filter-label">Range</span>
                    <button className="range active" type="button">YTD</button>
                </div>
            </div>
            <div id="app">{content}</div>
        </main>
    );
}
